// The Live stage, as pure functions (DESIGN.md "Live"): matching files to the
// brief's channels, the not-placed rule, when the kit can go, what is locked
// once a post is live, the status line under each post (green only for live),
// what sits on the media (a logo, never a channel's name), the timeline, and
// the adapter from today's distribution read. React-free and type-only
// imports, so scripts/test-live.mjs bundles this file on its own.

import type { CalendarPlatform } from "@/types/contentCalendar.type";
import { visibleErrorText } from "@/lib/errorText";
import {
  distributionFiles,
  distributionPublications,
  type DeliverableUrls,
  type DistributionResult,
  type PlatformFile,
  type Publication,
  type ChannelShape,
  type PublicationFields,
  type SkippedChannelReason,
  type SlotDeliverable,
  type SlotFile,
} from "@/types/scheduling.type";
import type { PostStateLive } from "../postState";
import { platformTakesCover } from "../coverRules";
import type {
  DistributionLiveBlock,
  LiveChannel,
  LiveData,
  LiveFile,
  LiveMatch,
  LiveMetrics,
  LivePost,
  LiveQuestion,
  LiveRoute,
  LiveUnplaced,
} from "./liveTypes";

/** The drag type a file tile carries from Files to a slide: the file's id. */
export const LIVE_FILE_DRAG = "application/x-creativeos-live-file";

// ------------------------------------------------------------------ words

export const PLATFORM_NAME: Record<string, string> = {
  TIKTOK: "TikTok",
  INSTAGRAM: "Instagram",
  YOUTUBE: "YouTube",
  FACEBOOK: "Facebook",
  SNAPCHAT: "Snapchat",
  LINKEDIN: "LinkedIn",
  PINTEREST: "Pinterest",
  REDDIT: "Reddit",
  SUBSTACK: "Substack",
};

const PLACEMENT_WORD = { REEL: "Reel", FEED: "feed", STORY: "Story", POST: "" } as const;

/** "TikTok", "Instagram Reel", "Instagram feed". For menus and sentences, never on media. */
export function channelName(channel: Pick<LiveChannel, "platform" | "placement">): string {
  const platform = PLATFORM_NAME[channel.platform] ?? channel.platform;
  const placement = channel.placement ? PLACEMENT_WORD[channel.placement] : "";
  return placement ? `${platform} ${placement}` : platform;
}

/** The placement's own word, sentence-initial ("Reel", "Feed"), or null. */
function placementLead(channel: LiveChannel): string | null {
  const word = channel.placement ? PLACEMENT_WORD[channel.placement] : "";
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : null;
}

/** "0:45", "1:05". */
export function durationLabel(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return null;
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** The brief's ask for one channel: "9:16 video, 0:45", "Reel, 9:16 video, 0:45". */
export function briefLine(channel: LiveChannel): string {
  const noun = channel.kind === "VIDEO" ? "video" : channel.kind === "IMAGE" ? "image" : "carousel";
  const shape = channel.kind === "CAROUSEL" && channel.slides?.length
    ? `${channel.slides.length} slides at ${channel.ratio}`
    : `${channel.ratio} ${noun}`;
  const length = channel.kind === "VIDEO" ? durationLabel(channel.lengthSeconds) : null;
  const lead = placementLead(channel);
  return [lead, shape, length].filter(Boolean).join(", ");
}

const KNOWN_RATIOS: Array<[string, number]> = [
  ["9:16", 9 / 16],
  ["4:5", 4 / 5],
  ["2:3", 2 / 3],
  ["3:4", 3 / 4],
  ["1:1", 1],
  ["4:3", 4 / 3],
  ["16:9", 16 / 9],
];

/** The nearest named ratio within 2%, else null. */
export function ratioOf(width: number | null | undefined, height: number | null | undefined): string | null {
  if (!width || !height) return null;
  const value = width / height;
  let best: [string, number] | null = null;
  for (const [name, ratio] of KNOWN_RATIOS) {
    const off = Math.abs(value - ratio) / ratio;
    if (off <= 0.02 && (!best || off < best[1])) best = [name, off];
  }
  return best ? best[0] : null;
}

/** 57600 → "57.6K", 4900 → "4.9K", 291 → "291", 1250000 → "1.3M". */
export function compactNumber(value: number): string {
  const abs = Math.abs(value);
  const fixed = (n: number) => {
    const text = n >= 100 ? String(Math.round(n)) : n.toFixed(1).replace(/\.0$/, "");
    return text;
  };
  if (abs >= 1_000_000) return `${fixed(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${fixed(value / 1_000)}K`;
  return String(Math.round(value));
}

/** "@sara.cooks" → "SA", "@noura.f" → "NF". */
export function initials(handle: string): string {
  const parts = handle.replace(/^@/, "").split(/[._\s-]+/).filter(Boolean);
  if (parts.length >= 2 && parts[1].length === 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0] ?? "").slice(0, 2).toUpperCase();
}

function timeParts(iso: string, timeZone: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const hm = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone });
  const weekday = date.toLocaleDateString("en-GB", { weekday: "short", timeZone });
  const day = date.toLocaleDateString("en-GB", { day: "numeric", timeZone });
  const month = date.toLocaleDateString("en-GB", { month: "short", timeZone });
  return { hm, weekday, day, month };
}

/** "18:02", or "Thu 18:02" with the day. */
export function clockLabel(iso: string, timeZone = "UTC", withDay = false): string {
  const parts = timeParts(iso, timeZone);
  if (!parts) return "";
  return withDay ? `${parts.weekday} ${parts.hm}` : parts.hm;
}

/** "Wed 9 Dec". */
export function dayLabel(iso: string, timeZone = "UTC"): string {
  const parts = timeParts(iso, timeZone);
  return parts ? `${parts.weekday} ${parts.day} ${parts.month}` : "";
}

// ------------------------------------------------------------------ matching

export interface MatchResult {
  matches: LiveMatch[];
  unplaced: LiveUnplaced[];
  question: LiveQuestion | null;
}

const LENGTH_TOLERANCE_SECONDS = 1;

function fitsChannel(file: LiveFile, channel: LiveChannel): boolean {
  const kind = channel.kind === "CAROUSEL" ? "IMAGE" : channel.kind;
  if (file.kind !== kind || file.ratio !== channel.ratio) return false;
  if (kind === "VIDEO" && channel.lengthSeconds !== null && file.durationSeconds !== null) {
    return Math.abs(file.durationSeconds - channel.lengthSeconds) <= LENGTH_TOLERANCE_SECONDS;
  }
  return true;
}

const words = (text: string) => text.toLowerCase().replace(/\.[a-z0-9]+$/, "").split(/[^a-z0-9]+/).filter(Boolean);

/** The slide a file's name points at: "cover" is slide 1, a label word, or a slide number. */
function slideHint(file: LiveFile, slides: { index: number; label: string }[]): number | null {
  const name = words(file.name);
  if (name.includes("cover")) return 1;
  for (const slide of slides) {
    const label = words(slide.label).filter((word) => !["the", "a", "your"].includes(word));
    if (label.length && label.every((word) => name.includes(word))) return slide.index;
    if (label.some((word) => word.length > 3 && name.includes(word))) return slide.index;
  }
  const number = name.map(Number).find((n) => Number.isInteger(n) && n >= 1 && n <= slides.length);
  return number ?? null;
}

/** A placement a file's name asks for that the brief does not have ("story"). */
function missingPlacement(file: LiveFile, channels: LiveChannel[]): string | null {
  const name = words(file.name);
  const asked = (["story", "reel", "feed", "pin"] as const).find((word) => name.includes(word));
  if (!asked) return null;
  const has = channels.some((channel) =>
    asked === "pin" ? channel.platform === "PINTEREST" : channel.placement?.toLowerCase() === asked,
  );
  return has ? null : asked === "pin" ? "Pin" : asked.charAt(0).toUpperCase() + asked.slice(1);
}

/**
 * Match files to the brief's channels (ratio, length, cover), in upload order.
 * A file that fits a slot another file already holds, or fits nothing, goes to
 * the not-placed tray with a reason; nothing is dropped. Two pictures that
 * could both be a carousel's cover become a question instead of a guess.
 */
export function matchFiles(channels: LiveChannel[], files: LiveFile[]): MatchResult {
  const matches: LiveMatch[] = [];
  const unplaced: LiveUnplaced[] = [];
  let question: LiveQuestion | null = null;
  const used = new Set<string>();
  const place = (match: LiveMatch) => {
    matches.push(match);
    used.add(match.fileId);
  };

  // 1. Videos and single images: one file per shape serves every channel of that shape.
  const singles = channels.filter((channel) => channel.kind !== "CAROUSEL" && !channel.recutOf);
  const groups = new Map<string, LiveChannel[]>();
  for (const channel of singles) {
    const key = `${channel.kind}|${channel.ratio}|${channel.lengthSeconds ?? ""}`;
    groups.set(key, [...(groups.get(key) ?? []), channel]);
  }
  for (const group of groups.values()) {
    const fits = files.filter((file) => !used.has(file.id) && fitsChannel(file, group[0]));
    const [first, ...rest] = fits;
    if (!first) continue;
    place({ fileId: first.id, channelIds: group.map((channel) => channel.id), role: "main" });
    for (const extra of rest) {
      used.add(extra.id);
      unplaced.push({
        fileId: extra.id,
        note: `a newer cut of the ${channelName(group[0])} file?`,
        insteadOf: first.id,
        insteadLabel: "Use instead of the first cut",
      });
    }
  }

  // 2. Covers: one picture at a video's ratio covers the videos of that ratio.
  const wantCover = singles.filter((channel) => channel.kind === "VIDEO" && channel.wantsCover);
  const coverRatios = [...new Set(wantCover.map((channel) => channel.ratio))];
  for (const ratio of coverRatios) {
    const covered = wantCover.filter((channel) => channel.ratio === ratio);
    const pictures = files.filter((file) => !used.has(file.id) && file.kind === "IMAGE" && (file.ratio === ratio || words(file.name).includes("cover")));
    const [first, ...rest] = pictures;
    if (!first) continue;
    place({ fileId: first.id, channelIds: covered.map((channel) => channel.id), role: "cover" });
    for (const extra of rest) {
      if (words(extra.name).includes("cover")) {
        used.add(extra.id);
        unplaced.push({ fileId: extra.id, note: "a second cover?", insteadOf: first.id, insteadLabel: "Use as the cover instead" });
      }
    }
  }

  // 3. Carousels: slides in the brief's order; two covers make a question.
  for (const channel of channels.filter((entry) => entry.kind === "CAROUSEL")) {
    const slides = channel.slides ?? [];
    const pictures = files.filter((file) => !used.has(file.id) && fitsChannel(file, channel));
    const bySlide = new Map<number, LiveFile[]>();
    const loose: LiveFile[] = [];
    for (const file of pictures) {
      const hint = slideHint(file, slides);
      if (hint) bySlide.set(hint, [...(bySlide.get(hint) ?? []), file]);
      else loose.push(file);
    }
    // A picture with no hint of its own that shares a subject with a hinted one
    // ("dumplings") is a rival for that slide, not a filler for an empty one.
    const subject = (file: LiveFile) => words(file.name).filter((word) => word.length > 3 && !/\d/.test(word));
    for (const file of [...loose]) {
      const own = subject(file);
      const rival = [...bySlide.entries()].find(([, held]) => held.some((other) => subject(other).some((word) => own.includes(word))));
      if (!rival) continue;
      bySlide.set(rival[0], [...rival[1], file]);
      loose.splice(loose.indexOf(file), 1);
    }
    for (const slide of slides) {
      const candidates = bySlide.get(slide.index) ?? [];
      if (candidates.length === 0 && loose.length) candidates.push(loose.shift()!);
      if (candidates.length === 1) {
        place({ fileId: candidates[0].id, channelIds: [channel.id], role: "slide", slide: slide.index });
      } else if (candidates.length > 1 && !question) {
        for (const file of candidates) used.add(file.id);
        question = {
          id: `${channel.id}-slide-${slide.index}`,
          question: `Which picture is slide ${slide.index}, ${slide.label.toLowerCase()}?`,
          hint: null,
          bannerLine: `${candidates.length === 2 ? "Two" : candidates.length} pictures could be ${slide.label.toLowerCase()}. Pick one and the kit is ready to send.`,
          options: candidates.map((file) => ({ fileId: file.id, label: file.name })),
          channelId: channel.id,
          slide: slide.index,
        };
      } else if (candidates.length > 1) {
        place({ fileId: candidates[0].id, channelIds: [channel.id], role: "slide", slide: slide.index });
      }
    }
  }

  // 4. Re-cuts (a Pinterest cut of the cover): a picture at the channel's own ratio.
  for (const channel of channels.filter((entry) => entry.recutOf)) {
    const file = files.find((entry) => !used.has(entry.id) && fitsChannel(entry, channel));
    if (file) place({ fileId: file.id, channelIds: [channel.id], role: "main" });
  }

  // 5. Whatever is left waits in the tray, with the reason.
  for (const file of files) {
    if (used.has(file.id)) continue;
    const missing = missingPlacement(file, channels);
    unplaced.push({
      fileId: file.id,
      note: missing
        ? `the brief has no ${missing}`
        : `no channel in the brief takes ${file.ratio ?? "this size"}${file.kind === "VIDEO" && file.durationSeconds !== null ? ` at ${durationLabel(file.durationSeconds)}` : ""}`,
    });
  }

  return { matches, unplaced, question };
}

/** The main file (or first slide) each channel shows. */
export function fileForChannel(data: Pick<LiveData, "matches">, channelId: string): string | null {
  const own = data.matches.filter((match) => match.channelIds.includes(channelId) && match.role !== "cover");
  const first = own.find((match) => match.role === "main") ?? own.sort((a, b) => (a.slide ?? 0) - (b.slide ?? 0))[0];
  return first?.fileId ?? null;
}

/** The still on a channel: its own cover picture when it has one, else the video's generated frame. */
export function pictureForChannel(data: Pick<LiveData, "matches">, channelId: string): string | null {
  const cover = data.matches.find(
    (match) => match.role === "cover" && match.channelIds.includes(channelId) && !match.fileId.endsWith(":cover"),
  );
  return cover?.fileId ?? fileForChannel(data, channelId);
}

/** A carousel's slides in order, with the file on each (null for an empty slot). */
export function slidesFor(data: Pick<LiveData, "matches" | "channels">, channelId: string) {
  const channel = data.channels.find((entry) => entry.id === channelId);
  return (channel?.slides ?? []).map((slide) => ({
    ...slide,
    fileId: data.matches.find((match) => match.role === "slide" && match.slide === slide.index && match.channelIds.includes(channelId))?.fileId ?? null,
  }));
}

// ------------------------------------------------------------------ rules

export type KitBlock = "uploading" | "tray" | "question" | "missing" | "unsupported" | "sent";

/** Whether the kit can go to the client now, and if not, the first reason. */
export function canSendKit(data: LiveData, uploadsInFlight = 0): { ok: boolean; reason: KitBlock | null } {
  if (!data.capabilities.kit) return { ok: false, reason: "unsupported" };
  if (data.kit?.sentAt) return { ok: false, reason: "sent" };
  if (uploadsInFlight > 0 || data.files.some((file) => file.status === "uploading" || file.status === "processing")) {
    return { ok: false, reason: "uploading" };
  }
  if (data.unplaced.length > 0) return { ok: false, reason: "tray" };
  if (data.question) return { ok: false, reason: "question" };
  const empty = data.channels.some((channel) => {
    if (channel.kind === "CAROUSEL") return slidesFor(data, channel.id).some((slide) => !slide.fileId);
    return !fileForChannel(data, channel.id);
  });
  if (empty || data.channels.length === 0) return { ok: false, reason: "missing" };
  return { ok: true, reason: null };
}

/** A post Fatafeat already put up: its channel and file are locked; a caption fix is not. */
export function isLocked(post: Pick<LivePost, "liveSince">): boolean {
  return !!post.liveSince;
}

export type PostTone = "live" | "changed" | "waiting";

/**
 * The line under a post once the kit is out. Only a post found live reads
 * "live", and "live" is the only tone that renders green.
 */
export function postStatus(
  post: Pick<LivePost, "liveSince" | "captionChangedAt">,
  options: { kitSent: boolean; timeZone?: string; withDay?: boolean },
): { tone: PostTone; text: string } | null {
  const zone = options.timeZone ?? "UTC";
  if (post.liveSince) {
    // A post up since an earlier day says which day.
    const withDay = options.withDay || Date.now() - new Date(post.liveSince).getTime() > 20 * 60 * 60 * 1000;
    return { tone: "live", text: `Live since ${clockLabel(post.liveSince, zone, withDay)}` };
  }
  if (!options.kitSent) return null;
  if (post.captionChangedAt) {
    return { tone: "changed", text: `Caption updated ${clockLabel(post.captionChangedAt, zone)} · not posted yet` };
  }
  return { tone: "waiting", text: "Not posted yet" };
}

/** The ink for a tone. Green (`--co-live`) belongs to live alone. */
export function toneClass(tone: PostTone): string {
  return tone === "live" ? "text-co-live" : "text-co-muted";
}

/**
 * What sits on a post's picture: the platform mark (its name is for a screen
 * reader only), then the handle and the caption. No channel's name is ever
 * written on media.
 */
export function mediaOverlay(post: Pick<LivePost, "handle" | "caption">, channel: Pick<LiveChannel, "platform" | "placement">) {
  return {
    chip: { platform: channel.platform, srLabel: channelName(channel) },
    lines: [post.handle ? (post.handle.startsWith("@") ? post.handle : `@${post.handle}`) : null, post.caption || null].filter(
      (line): line is string => !!line,
    ),
  };
}

/** Channels whose numbers CreativeOS reads (backend livePostLinks.ts METRICS_PLATFORMS). */
export const NUMBERS_PLATFORMS: CalendarPlatform[] = ["TIKTOK", "INSTAGRAM", "YOUTUBE", "FACEBOOK"];

export type MetricKind = "views" | "likes" | "saves" | "comments" | "shares";

const METRIC_ORDER: MetricKind[] = ["views", "likes", "comments", "shares", "saves"];

/** Every number a live post has, in one order. A number the source cannot give is left out. */
export function metricList(metrics: LiveMetrics | null | undefined): { kind: MetricKind; value: number }[] {
  if (!metrics) return [];
  return METRIC_ORDER.flatMap((kind) => {
    const value = metrics[kind];
    return typeof value === "number" ? [{ kind, value }] : [];
  });
}

/**
 * Each number summed across the post's live channels. A kind shows only when
 * at least one channel gives it, and says how many did: a total over two of
 * three channels must not read as all three.
 */
export function metricTotals(posts: Pick<LivePost, "metrics">[]): { kind: MetricKind; value: number; channels: number }[] {
  return METRIC_ORDER.flatMap((kind) => {
    const values = posts.map((post) => post.metrics?.[kind]).filter((value): value is number => typeof value === "number");
    return values.length ? [{ kind, value: values.reduce((sum, value) => sum + value, 0), channels: values.length }] : [];
  });
}

/** Where a post's numbers come from, in the team's words. */
export function metricsSourceLabel(source: LivePost["metricsSource"]): string | null {
  if (source === "CONNECTED") return "From the connected account";
  if (source === "PUBLIC") return "Public numbers only";
  return null;
}

/**
 * The line under a live post's numbers: a read running, when they were read
 * and when they are read next, or that they are no longer read on their own.
 * Null for a channel CreativeOS cannot read numbers for.
 */
export function numbersLine(
  post: Pick<LivePost, "tracking" | "metricsUpdatedAt">,
  options: { timeZone?: string; now?: number } = {},
): string | null {
  const tracking = post.tracking;
  if (!tracking) return null;
  const zone = options.timeZone ?? "UTC";
  const now = options.now ?? Date.now();
  // A time on another day says which day.
  const when = (iso: string) => clockLabel(iso, zone, Math.abs(new Date(iso).getTime() - now) > 20 * 60 * 60 * 1000);
  if (tracking.reading) return post.metricsUpdatedAt ? "Updating the numbers…" : "Reading the numbers…";
  if (!post.metricsUpdatedAt) return tracking.nextReadAt ? `Numbers read at ${when(tracking.nextReadAt)}` : null;
  const read = `Updated ${when(post.metricsUpdatedAt)}`;
  if (!tracking.nextReadAt) return `${read} · no longer checked`;
  return `${read} · next ${when(tracking.nextReadAt)}`;
}

/** The numbers on a result post's picture: views for a video, likes, then saves or comments. */
export function metricChips(metrics: LiveMetrics | null | undefined, channel: Pick<LiveChannel, "kind">): { kind: MetricKind; value: number }[] {
  if (!metrics) return [];
  const chips: { kind: MetricKind; value: number | null | undefined }[] = [];
  if (channel.kind === "VIDEO") chips.push({ kind: "views", value: metrics.views });
  chips.push({ kind: "likes", value: metrics.likes });
  if (metrics.saves !== null && metrics.saves !== undefined) chips.push({ kind: "saves", value: metrics.saves });
  else chips.push({ kind: "comments", value: metrics.comments });
  return chips.filter((chip): chip is { kind: MetricKind; value: number } => typeof chip.value === "number");
}

/** A ratio mismatch M7 flags after a swap ("will show with bars"). */
export function ratioFlag(channel: LiveChannel, file: LiveFile): string | null {
  if (!file.ratio || file.ratio === channel.ratio) return null;
  const where = channel.placement ? PLACEMENT_WORD[channel.placement] || channelName(channel) : channelName(channel);
  const lead = where.charAt(0).toUpperCase() + where.slice(1);
  return `The brief planned the ${lead} at ${channel.ratio}, so this ${file.ratio} file will show with bars. Your call.`;
}

// ------------------------------------------------------------------ banner and timeline

/** The Live stage's summary the status banner reads (postState `live`). */
export function bannerInput(data: LiveData, uploads: { inFlight: number; total: number } = { inFlight: 0, total: 0 }): PostStateLive {
  const uploading = uploads.inFlight + data.files.filter((file) => file.status === "uploading").length;
  const total = Math.max(uploads.total, uploading);
  const kit = !data.capabilities.kit ? "unsupported" : data.kit?.sentAt ? "sent" : canSendKit(data, uploads.inFlight).ok ? "ready" : "none";
  return {
    uploading,
    uploadTotal: total,
    filesIn: data.files.filter((file) => file.status !== "uploading").length,
    unplaced: data.unplaced.length,
    question: data.question ? data.question.bannerLine : null,
    kit,
    results: !!data.results,
    matching: data.capabilities.matching,
    channels: channelSummary(data),
  };
}

/** How far each channel got, for the Live banners (Figma 08, 6 Live). */
function channelSummary(data: LiveData): NonNullable<PostStateLive["channels"]> | undefined {
  if (!data.channels.length) return undefined;
  const now = Date.now();
  const rows = data.channels.map((channel) => ({ channel, post: data.posts.find((post) => post.channelId === channel.id) }));
  const routeOfRow = (row: (typeof rows)[number]) => row.post?.route ?? null;
  const earliest = (values: Array<string | null | undefined>) =>
    values.filter((value): value is string => !!value).sort((a, b) => Date.parse(a) - Date.parse(b))[0] ?? null;
  const failedRow = rows.find((row) => !row.post?.liveSince && routeOfRow(row)?.state === "failed");
  const selfDue = rows.find((row) => {
    const route = routeOfRow(row);
    return !row.post?.liveSince && route?.state === "manual" && route.selfPost && (!route.at || Date.parse(route.at) <= now);
  });
  const pending = rows.filter((row) => !row.post?.liveSince);
  return {
    total: rows.length,
    ready: pending.filter((row) => {
      const route = routeOfRow(row);
      return route?.state === "not-scheduled" && !!route.canSchedule && !!fileForChannel(data, row.channel.id);
    }).length,
    scheduled: pending.filter((row) => routeOfRow(row)?.state === "scheduled").length,
    posting: pending.filter((row) => routeOfRow(row)?.state === "posting").length,
    live: rows.filter((row) => row.post?.liveSince).length,
    goesOutAt: earliest(pending.map((row) => routeOfRow(row)?.at)) ?? data.goesOutAt,
    liveSince: earliest(rows.map((row) => row.post?.liveSince)),
    failed: failedRow
      ? {
          platform: PLATFORM_NAME[failedRow.channel.platform] ?? failedRow.channel.platform,
          reason: failedRow.post?.route?.error ?? null,
          attempts: failedRow.post?.route?.attempts ?? 0,
          nextAttemptAt: failedRow.post?.route?.nextAttemptAt ?? null,
        }
      : null,
    notConnected: [...new Set(pending.filter((row) => routeOfRow(row)?.state === "connect").map((row) => PLATFORM_NAME[row.channel.platform] ?? row.channel.platform))],
    reconnect: null,
    selfDue: selfDue ? channelName(selfDue.channel) : null,
    needs: [],
    carouselPictures: data.slides && data.slides.items.length > 1 ? data.slides.items.length : 0,
  };
}

export type StepState = "done" | "current" | "next" | "live";

export interface TimelineStep {
  id: "files" | "kit" | "out" | "live" | "results";
  label: string;
  sub: string;
  state: StepState;
}

/**
 * The four stops under the posts (library, 1 Oct): Files in, Scheduled, Live,
 * First 24 hours. "Scheduled" is when the posts go out; "Live" counts the
 * channels found live; the last stop is their numbers.
 */
export function timeline(
  data: LiveData,
  options: { clientName: string; timeZone?: string; uploadsInFlight?: number; uploadTotal?: number; compact?: boolean },
): TimelineStep[] {
  const zone = options.timeZone ?? "UTC";
  const placed = new Set(data.matches.map((match) => match.fileId)).size;
  const total = data.files.length;
  const inFlight = (options.uploadsInFlight ?? 0) + data.files.filter((file) => file.status === "uploading").length;
  const filesDone = total > 0 && inFlight === 0 && data.unplaced.length === 0 && !data.question;

  // "0 of 2 in": the files the channels' shapes call for, or the pile when it is bigger (Figma 08 Live).
  const shapes = new Set(data.channels.map((channel) => channel.ratio)).size;
  const wanted = Math.max(shapes, options.uploadTotal ?? total, total);
  const filesSub =
    data.unplaced.length > 0 || data.question
      ? `${placed} of ${total} placed`
      : `${Math.max(0, total - inFlight)} of ${wanted} in`;

  const routes = data.posts.map((post) => post.route ?? null);
  const live = data.posts.filter((post) => post.liveSince).length;
  const channels = data.posts.length;
  const allLive = channels > 0 && live === channels;
  // Every channel that is not live yet has its way out settled: scheduled,
  // posting, or posted by hand.
  const settled =
    channels > 0 &&
    data.posts.every((post, index) => !!post.liveSince || ["scheduled", "posting", "manual"].includes(routes[index]?.state ?? ""));
  const goes = data.goesOutAt;
  const city = data.timezoneCity ?? (zone.includes("/") ? zone.split("/").pop()!.replace(/_/g, " ") : zone);
  const scheduledCount = data.posts.filter((post, index) => !post.liveSince && routes[index]?.state === "scheduled").length;
  const outState =
    live > 0
      ? `${live} of ${channels} live`
      : scheduledCount > 0
        ? `${scheduledCount} scheduled`
        : "not scheduled";
  const when = goes ? `${dayLabel(goes, zone)}, ${clockLabel(goes, zone)} · ${outState}` : `When the posts are timed · ${outState}`;
  void city;
  const yours = data.posts.find((post, index) => !post.liveSince && routes[index]?.state === "manual");
  const yoursName = yours ? (PLATFORM_NAME[data.channels.find((channel) => channel.id === yours.channelId)?.platform ?? ""] ?? null) : null;
  const anyNumbers = data.posts.some((post) => post.metrics && metricList(post.metrics).length > 0);
  // Figma 08 (177:65940): "Live · 3 of 4, since 18:02", "First 24 hours · 14 hours in".
  const firstLive = data.posts.map((post) => post.liveSince).filter((value): value is string => !!value).sort()[0] ?? null;
  const sinceLive = firstLive ? Date.now() - new Date(firstLive).getTime() : 0;
  const hours = Math.floor(sinceLive / 3_600_000);
  const day = Math.floor(sinceLive / 86_400_000) + 1;
  const numbersSub = !anyNumbers
    ? "the numbers"
    : hours < 24
      ? hours < 1
        ? "under an hour in"
        : `${hours} ${hours === 1 ? "hour" : "hours"} in`
      : day <= 7
        ? `day ${day} of 7`
        : "the first week is in";

  return [
    { id: "files", label: "Files ready", sub: filesSub, state: filesDone ? "done" : "current" },
    {
      id: "out",
      // "Went out" once any channel is live (Figma 08, TikTok didn't post).
      label: live > 0 ? "Went out" : "Goes out",
      sub: when,
      state: live > 0 || (filesDone && settled) ? "done" : filesDone ? "current" : "next",
    },
    {
      id: "live",
      label: "Live",
      sub: yoursName
        ? `${yoursName}: yours to post`
        : firstLive
          ? `${allLive && channels > 1 ? `all ${channels}` : `${live} of ${channels}`}, since ${clockLabel(firstLive, zone)}`
          : "once they are posted",
      state: allLive ? "done" : live > 0 ? "live" : "next",
    },
    {
      id: "results",
      label: "First 24 hours",
      sub: numbersSub,
      state: live > 0 && anyNumbers ? "current" : "next",
    },
  ];
}

// ------------------------------------------------------------------ captions

type BodyKey = "caption" | "description";

function bodyKey(platform: CalendarPlatform): BodyKey {
  return platform === "YOUTUBE" || platform === "SNAPCHAT" ? "description" : "caption";
}

/**
 * One caption field: the body with its hashtags laid out after it. YouTube's
 * tags are keywords, not hashtags, so they stay out of the text (and are kept
 * as they are on save).
 */
export function captionOf(platform: CalendarPlatform, fields: PublicationFields | null | undefined): string {
  if (!fields) return "";
  const body = (fields[bodyKey(platform)] ?? "").trim();
  if (platform === "YOUTUBE") return body;
  const tags = (fields.hashtags ?? []).map((tag) => `#${tag.replace(/^#/, "")}`).join(" ");
  return [body, tags].filter(Boolean).join(" ");
}

/**
 * The fields one caption field changes. The hashtags live in the text the
 * person laid out, so the list is cleared. The caller merges this over the
 * publication's own fields, so nothing the editor does not show is lost.
 */
export function fieldsForCaption(platform: CalendarPlatform, text: string): PublicationFields {
  if (platform === "YOUTUBE") return { description: text };
  return bodyKey(platform) === "description" ? { description: text, hashtags: [] } : { caption: text, hashtags: [] };
}

// ------------------------------------------------------------------ the adapter

export interface LiveSource {
  slotId: string;
  mediaType: "STATIC" | "VIDEO" | string;
  ratio: string;
  lengthSeconds: number | null;
  channels: CalendarPlatform[];

  distribution: (DistributionResult & { live?: DistributionLiveBlock | null }) | null | undefined;
}

function deliverableFile(deliverable: SlotDeliverable & DeliverableUrls): LiveFile {
  const kind = deliverable.kind === "IMAGE" ? "IMAGE" : "VIDEO";
  return {
    id: deliverable.id,
    name: deliverable.fileName,
    kind,
    ratio: ratioOf(deliverable.width, deliverable.height),
    durationSeconds: deliverable.durationSeconds,
    thumbUrl: deliverable.thumbnailUrl ?? (kind === "IMAGE" ? deliverable.mediaUrl : null),
    mediaUrl: deliverable.mediaUrl,
    status:
      deliverable.status === "READY" ? "ready" : deliverable.status === "FAILED" ? "failed" : deliverable.status === "UPLOADING" ? "uploading" : "processing",
    error: deliverable.processingError,
    mimeType: deliverable.mimeType,
    sizeBytes: deliverable.sizeBytes,
  };
}

/**
 * How a channel goes out, from its publication, or from why it has none
 * (the read's skipped channels). Nothing to say once it is live.
 */
export function routeOf(
  publication: Publication | undefined,
  skipped: SkippedChannelReason | undefined,
  plannedAt: string | null,
  slotDay: string | null,
): LiveRoute | null {
  if (publication) {
    const at = publication.scheduledAt ?? plannedAt;
    // Post it yourself: waiting for the team, whatever the publication could do.
    if (publication.selfPost && (publication.status === "READY" || publication.status === "DRAFT")) {
      return { state: "manual", at, selfPost: true, editable: true };
    }
    switch (publication.status) {
      case "SCHEDULED":
        return { state: "scheduled", at, editable: true };
      case "PUBLISHING":
        return { state: "posting", at };
      case "FAILED":
        return {
          state: "failed",
          at,
          error: visibleErrorText(publication.lastError),
          attempts: publication.attempts,
          nextAttemptAt: publication.nextAttemptAt,
          editable: true,
        };
      case "READY":
        return { state: "not-scheduled", at, canSchedule: !!publication.socialAccountId, editable: true };
      case "DRAFT":
        return {
          state: "not-scheduled",
          at,
          canSchedule: false,
          editable: true,
          ...(publication.issues?.[0]?.code ? { blocked: publication.issues[0].code } : {}),
        };
      default:
        return null;
    }
  }
  if (skipped === "NO_ACCOUNT") return { state: "connect", at: plannedAt, day: plannedAt ? null : slotDay };
  if (skipped === "NOT_PUBLISHABLE") return { state: "manual", at: plannedAt, day: plannedAt ? null : slotDay };
  return null;
}

/** The words on a post's State chip: "Thu 18:00", "Scheduled · Thu 18:00", "Manual · Thu 18:00", "Failed", "Posting". */
export function routeChip(route: LiveRoute, timeZone: string): string | null {
  const when = route.at ? `${weekdayLabel(route.at, timeZone)} ${clockLabel(route.at, timeZone)}` : route.day ? weekdayLabel(route.day, "UTC") : null;
  switch (route.state) {
    case "scheduled":
      return when ? `Scheduled · ${when}` : "Scheduled";
    case "manual":
      return when ? `Manual · ${when}` : "Manual";
    case "not-scheduled":
      return when;
    case "posting":
      return "Posting";
    case "failed":
      return "Failed";
    default:
      return null;
  }
}

function weekdayLabel(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { weekday: "short", timeZone });
}

function publicationPost(channel: LiveChannel, publication: Publication | undefined, fileId: string | null, copy: PublicationFields | undefined): LivePost {
  // The post's own copy for the platform (SlotChannel) wins; a server without
  // it falls back to the publication's.
  const fields = copy ?? publication?.fields;
  return {
    channelId: channel.id,
    fileId,
    caption: captionOf(channel.platform, fields),
    title: fields?.title ?? "",
    coverTimestampMs: fields?.coverTimestampMs ?? null,
    publicationId: publication?.id ?? null,
    handle: publication?.accountName && !/\s/.test(publication.accountName) ? publication.accountName : null,
    liveSince: publication?.status === "PUBLISHED" ? publication.publishedAt : null,
    permalink: publication?.permalink ?? null,
  };
}

/** A video's cover as its own row (the backend keeps the cover, not the name it was uploaded under). */
function coverFile(file: SlotFile): LiveFile | null {
  return file.kind === "VIDEO" && file.thumbnailUrl
    ? { id: `${file.id}:cover`, name: "The cover", kind: "IMAGE", ratio: null, durationSeconds: null, thumbUrl: file.thumbnailUrl, mediaUrl: null, status: "ready" }
    : null;
}

/**
 * The file each channel shows. Once the post's files were matched, the
 * platform's match (even before that file is checked). Otherwise its
 * publication's file; a channel with no publication takes the file uploaded
 * for its platform, else the default when that is a file for every platform
 * (the server's display default can be another platform's file, which this
 * channel would never get); with neither it is empty. A picture on a video
 * post is never a channel's file.
 */
function channelFiles(
  channels: LiveChannel[],
  files: SlotFile[],
  publications: Publication[],
  platformFiles: PlatformFile[],
  mainKind: "IMAGE" | "VIDEO",
): Map<string, string | null> {
  const known = new Set(files.map((file) => file.id));
  const mains = files.filter((file) => file.kind === mainKind);
  const ready = mains.filter((file) => file.status === "READY");
  const fallback = (platform: CalendarPlatform) =>
    (ready.find((file) => file.forPlatform === platform) ??
      mains.find((file) => file.isDefault && file.forPlatform === null) ??
      null)?.id ?? null;
  return new Map(
    channels.map((channel) => {
      const row = platformFiles.find((entry) => entry.platform === channel.platform);
      if (row) return [channel.id, row.deliverableId && known.has(row.deliverableId) ? row.deliverableId : null];
      const publication = publications.find((entry) => entry.platform === channel.platform);
      const fileId = publication && known.has(publication.deliverableId) ? publication.deliverableId : fallback(channel.platform);
      return [channel.id, fileId];
    }),
  );
}

/** The cover picture each channel's match picked, if any. */
function channelCovers(channels: LiveChannel[], files: SlotFile[], platformFiles: PlatformFile[]): Map<string, string | null> {
  const known = new Set(files.map((file) => file.id));
  return new Map(
    channels.map((channel) => {
      // A platform that takes no cover picture (TikTok) shows none, even from an older match.
      if (!platformTakesCover(channel.platform)) return [channel.id, null];
      const cover = platformFiles.find((entry) => entry.platform === channel.platform)?.coverDeliverableId ?? null;
      return [channel.id, cover && known.has(cover) ? cover : null];
    }),
  );
}

/** Why a matched post left a file on no channel. */
function unmatchedNote(file: SlotFile, mainKind: "IMAGE" | "VIDEO"): string {
  return file.kind === mainKind ? "another file was matched to every channel" : "another picture is the cover";
}

/**
 * The Live view model from the distribution read: every file on the post,
 * each channel showing its own publication's file (with a video's cover), a
 * caption per publication and a publish time. Each piece of the optional
 * `live` block replaces its own part when the backend sends it.
 */
export function liveFromDistribution(source: LiveSource): LiveData {
  const live = source.distribution?.live ?? null;
  const deliverable = source.distribution?.deliverable ?? null;
  const serverFiles = distributionFiles(source.distribution);
  const publications = distributionPublications(source.distribution);
  const kind = source.mediaType === "STATIC" ? "IMAGE" : "VIDEO";
  // The post's slides (two or more make a carousel), and what each platform posts.
  const items = source.distribution?.items ?? [];
  const carousel = kind === "IMAGE" && items.length >= 2;
  const shapes: Partial<Record<string, ChannelShape>> = source.distribution?.channelShapes ?? {};
  const slideList = items.map((item, index) => ({ index: index + 1, label: item.label || `Slide ${index + 1}`, itemId: item.id }));
  const channels: LiveChannel[] =
    live?.channels ??
    source.channels.map((platform) => {
      const shape = shapes[platform] ?? kind;
      return {
        id: platform,
        platform,
        placement: null,
        kind: shape,
        ratio: source.ratio,
        lengthSeconds: kind === "VIDEO" ? source.lengthSeconds : null,
        wantsCover: kind === "VIDEO" && platformTakesCover(platform),
        ...(shape === "CAROUSEL" ? { slides: slideList } : {}),
      };
    });
  const carouselIds = channels.filter((channel) => channel.kind === "CAROUSEL").map((channel) => channel.id);
  // Every file on the post; each file serves the channels its match (or
  // publication) gives it. A channel's cover is the picture its match picked,
  // else its video's own cover.
  const platformFiles = (source.distribution?.platformFiles ?? []).filter((row) =>
    channels.some((channel) => channel.platform === row.platform),
  );
  const fileOf = channelFiles(channels, serverFiles, publications, platformFiles, kind);
  // A carousel channel shows its slides, not one file.
  for (const id of carouselIds) fileOf.set(id, null);
  const onSlide = new Set(items.map((item) => item.deliverableId).filter((id): id is string => !!id));
  const coverOf = channelCovers(channels, serverFiles, platformFiles);
  const readFiles: LiveFile[] = [];
  const readMatches: LiveMatch[] = [];
  const readUnplaced: LiveUnplaced[] = [];
  for (const file of serverFiles) {
    readFiles.push(deliverableFile(file));
    const mainFor = channels.filter((channel) => fileOf.get(channel.id) === file.id).map((channel) => channel.id);
    const coverFor = channels.filter((channel) => coverOf.get(channel.id) === file.id).map((channel) => channel.id);
    if (mainFor.length) readMatches.push({ fileId: file.id, channelIds: mainFor, role: "main" });
    if (coverFor.length) readMatches.push({ fileId: file.id, channelIds: coverFor, role: "cover" });
    const own = coverFile(file);
    // The video's own cover shows on its channels without a cover picture, except
    // the ones that take no cover at all (TikTok uses a frame of the video).
    const bare = mainFor.filter(
      (channelId) =>
        !coverOf.get(channelId) &&
        platformTakesCover(channels.find((channel) => channel.id === channelId)?.platform ?? ""),
    );
    if (own && bare.length) {
      readFiles.push(own);
      readMatches.push({ fileId: own.id, channelIds: bare, role: "cover" });
    }
    const slideAt = items.findIndex((item) => item.deliverableId === file.id);
    if (slideAt >= 0 && carouselIds.length) {
      readMatches.push({ fileId: file.id, channelIds: carouselIds, role: "slide", slide: slideAt + 1 });
    }
    if (carousel && kind === file.kind && !mainFor.length && !onSlide.has(file.id) && file.status !== "FAILED") {
      readUnplaced.push({ fileId: file.id, note: "on no slide yet" });
    } else if (platformFiles.length && !mainFor.length && !coverFor.length && slideAt < 0 && file.status !== "FAILED") {
      readUnplaced.push({ fileId: file.id, note: unmatchedNote(file, kind) });
    }
  }
  const files: LiveFile[] = live?.files ?? readFiles;
  const matches: LiveMatch[] = live?.matches ?? readMatches;
  const firstScheduled = publications.map((entry) => entry.scheduledAt).filter(Boolean).sort()[0] ?? null;
  const skipped = new Map((source.distribution?.skippedChannels ?? []).map((entry) => [entry.platform, entry.reason]));
  const coverLocks = new Map((source.distribution?.coverLocks ?? []).map((entry) => [entry.platform, entry.reason]));
  const slotDay = source.distribution?.slotDay ?? null;
  const posts = channels.map((channel) => {
    const publication = publications.find((entry) => entry.platform === channel.platform);
    const copy = source.distribution?.channelCopy?.find((row) => row.platform === channel.platform)?.copy;
    const base = publicationPost(channel, publication, fileForChannel({ matches }, channel.id), copy);
    const extra = live?.posts?.find((entry) => entry.channelId === channel.id);
    const post = extra ? { ...base, ...extra } : base;
    const route = routeOf(publication, skipped.get(channel.platform), firstScheduled, slotDay);
    // A hand-posted channel keeps its route after it is live, so the hand stays on the picture.
    if (post.liveSince) return route?.state === "manual" ? { ...post, route } : post;
    const coverLock = channel.kind === "VIDEO" ? (coverLocks.get(channel.platform) ?? null) : null;
    return { ...post, route, coverLock };
  });
  return {
    channels,
    files,
    matches,
    unplaced: live?.unplaced ?? readUnplaced,
    posts,
    question: live?.question ?? null,
    checks: live?.checks ?? null,
    kit: live?.kit ?? null,
    results: live?.results ?? null,
    filesInAt: live?.filesInAt ?? deliverable?.createdAt ?? serverFiles[serverFiles.length - 1]?.createdAt ?? null,
    goesOutAt: live?.goesOutAt ?? firstScheduled,
    timezoneCity: live?.timezoneCity ?? null,
    capabilities: {
      // M7's matching arrives as its own pieces; a `live` block that carries
      // only the posts' links and numbers is not matching.
      matching: !!live?.matches,
      kit: live?.kit !== undefined,
      swap: !!live?.matches,
      addChannel: !!live?.channels,
      reorder: !!live?.matches || carousel,
      removePlaced: !!live?.files,
      results: live?.results !== undefined,
      copy: !!source.distribution?.channelCopy,
    },
    ...(kind === "IMAGE" && source.distribution?.items
      ? {
          slides: {
            items: items.map((item) => ({
              id: item.id,
              label: item.label,
              fileId: item.deliverableId,
              briefPanelId: item.briefPanelId,
            })),
            maxSlides: Object.values(source.distribution.carouselLimits ?? {}).reduce<number | null>(
              (most, limit) => (limit && (most === null || limit > most) ? limit : most),
              null,
            ),
          },
        }
      : {}),
  };
}

// ------------------------------------------------------------------ edits (the harness's fixture mutations)

/** Re-run the match over a changed set of files. */
export function rematch(data: LiveData, files: LiveFile[]): LiveData {
  const result = matchFiles(data.channels, files);
  const posts = data.posts.map((post) => ({ ...post, fileId: fileForChannel(result, post.channelId) }));
  return { ...data, files, ...result, posts };
}

/** Give a channel another file. Returns the flag M7 raises when the shape is off. */
export function swapFile(data: LiveData, channelId: string, fileId: string): { data: LiveData; flag: string | null; previous: string | null } {
  const channel = data.channels.find((entry) => entry.id === channelId);
  const file = data.files.find((entry) => entry.id === fileId);
  if (!channel || !file) return { data, flag: null, previous: null };
  const previous = fileForChannel(data, channelId);
  const flag = ratioFlag(channel, file);
  const matches = data.matches
    .map((match) =>
      match.role === "main" && match.channelIds.includes(channelId)
        ? { ...match, channelIds: match.channelIds.filter((id) => id !== channelId) }
        : match,
    )
    .filter((match) => match.channelIds.length > 0);
  const existing = matches.find((match) => match.fileId === fileId && match.role === "main");
  const next = existing
    ? matches.map((match) => (match === existing ? { ...match, channelIds: [...match.channelIds, channelId], flag: flag ?? match.flag ?? null } : match))
    : [...matches, { fileId, channelIds: [channelId], role: "main" as const, flag }];
  const posts = data.posts.map((post) => (post.channelId === channelId ? { ...post, fileId } : post));
  return { data: { ...data, matches: next, posts }, flag, previous };
}

/** Answer M7's question: the pick fills the slot, the other pictures wait in the tray. */
export function answerQuestion(data: LiveData, fileId: string): LiveData {
  const question = data.question;
  if (!question) return data;
  const match: LiveMatch = question.slide
    ? { fileId, channelIds: [question.channelId], role: "slide", slide: question.slide }
    : { fileId, channelIds: [question.channelId], role: "cover" };
  const others: LiveUnplaced[] = question.options
    .filter((option) => option.fileId !== fileId)
    .map((option) => ({ fileId: option.fileId, note: "not picked for the slot", insteadOf: fileId, insteadLabel: "Use it instead" }));
  return { ...data, question: null, matches: [...data.matches, match], unplaced: [...data.unplaced, ...others] };
}

/** Put a tray file in place of a placed one; the replaced file leaves the post. */
export function placeInstead(data: LiveData, trayFileId: string, placedFileId: string): LiveData {
  const matches = data.matches.map((match) => (match.fileId === placedFileId ? { ...match, fileId: trayFileId, flag: null } : match));
  return {
    ...data,
    files: data.files.filter((file) => file.id !== placedFileId),
    matches,
    unplaced: data.unplaced.filter((entry) => entry.fileId !== trayFileId),
    posts: data.posts.map((post) => (post.fileId === placedFileId ? { ...post, fileId: trayFileId } : post)),
  };
}

/** Remove a file from the post: from the tray, or from every slot it held. */
export function removeFile(data: LiveData, fileId: string): LiveData {
  return {
    ...data,
    files: data.files.filter((file) => file.id !== fileId),
    matches: data.matches.filter((match) => match.fileId !== fileId),
    unplaced: data.unplaced.filter((entry) => entry.fileId !== fileId),
    posts: data.posts.map((post) => (post.fileId === fileId ? { ...post, fileId: null } : post)),
  };
}

/** Move a carousel slide from one position to another; the files follow their new numbers. */
export function reorderSlides(data: LiveData, channelId: string, from: number, to: number): LiveData {
  const order = slidesFor(data, channelId).map((slide) => slide.fileId);
  if (from < 1 || to < 1 || from > order.length || to > order.length || from === to) return data;
  const [moved] = order.splice(from - 1, 1);
  order.splice(to - 1, 0, moved);
  const rest = data.matches.filter((match) => !(match.role === "slide" && match.channelIds.includes(channelId)));
  const slides = order
    .map((fileId, index) => (fileId ? { fileId, channelIds: [channelId], role: "slide" as const, slide: index + 1 } : null))
    .filter((match): match is NonNullable<typeof match> => !!match);
  return { ...data, matches: [...rest, ...slides] };
}

/** Two posts trade channels: each channel takes the other's file and caption. */
export function swapChannels(data: LiveData, channelId: string, toChannelId: string): LiveData {
  if (channelId === toChannelId) return data;
  const a = data.posts.find((post) => post.channelId === channelId);
  const b = data.posts.find((post) => post.channelId === toChannelId);
  if (!a || !b) return data;
  const trade = (id: string) => (id === channelId ? toChannelId : id === toChannelId ? channelId : id);
  const matches = data.matches.map((match) =>
    match.role === "main" ? { ...match, channelIds: match.channelIds.map(trade) } : match,
  );
  const posts = data.posts.map((post) =>
    post.channelId === channelId
      ? { ...post, fileId: b.fileId, caption: b.caption }
      : post.channelId === toChannelId
        ? { ...post, fileId: a.fileId, caption: a.caption }
        : post,
  );
  return { ...data, matches, posts };
}
